import React, { useState } from 'react';
import {
  Typography, Spin, Empty, Button, Space, Drawer,
} from 'antd';
import {
  FireOutlined, ClockCircleOutlined, StarOutlined, PercentageOutlined,
  PlusOutlined, FilterOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useListDealsQuery } from './dealsApi';
import DealCard from '../../../../../packages/ui/src/DealCard/index';
import DealFilterPanel from '../../../../../packages/ui/src/DealFilterPanel/index';

const { Text } = Typography;

const SORT_OPTIONS = [
  { value: 'trending', label: 'Trending', icon: <FireOutlined /> },
  { value: 'new', label: 'Newest', icon: <ClockCircleOutlined /> },
  { value: 'ending-soon', label: 'Ending Soon', icon: <StarOutlined /> },
  { value: 'discount', label: 'Biggest Discount', icon: <PercentageOutlined /> },
];

export default function DealListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const sort = searchParams.get('sort') || 'trending';
  const search = searchParams.get('search') || searchParams.get('q') || '';
  const cursor = searchParams.get('cursor') || undefined;
  const condition = searchParams.get('condition') || undefined;
  const freeShipping = searchParams.get('freeShipping') === '1' || undefined;
  const maxPrice = searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) * 100 : undefined;
  const category = searchParams.get('category') || undefined;

  const { data, isLoading, isFetching } = useListDealsQuery({
    sort, search, cursor, limit: 30, condition, freeShipping, maxPrice, category,
  });

  const handleSortChange = (value) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', value);
    params.delete('cursor');
    setSearchParams(params);
  };

  const handleFilters = (filters) => {
    const params = new URLSearchParams(searchParams);
    params.delete('cursor');
    if (filters.category) params.set('category', filters.category);
    else params.delete('category');
    if (filters.maxPrice) params.set('maxPrice', String(filters.maxPrice));
    else params.delete('maxPrice');
    if (filters.freeShipping) params.set('freeShipping', '1');
    else params.delete('freeShipping');
    if (filters.condition) params.set('condition', filters.condition);
    else params.delete('condition');
    if (filters.sortBy && filters.sortBy !== 'hot') {
      const map = { new: 'new', expiring: 'ending-soon', top: 'trending', price_asc: 'discount' };
      params.set('sort', map[filters.sortBy] || 'trending');
    }
    setSearchParams(params);
    setFiltersOpen(false);
  };

  const handleLoadMore = () => {
    if (data?.page?.hasMore) {
      const params = new URLSearchParams(searchParams);
      params.set('cursor', data.page.nextCursor);
      setSearchParams(params);
    }
  };

  const filterValues = {
    category,
    maxPrice: maxPrice ? maxPrice / 100 : 500,
    freeShipping: !!freeShipping,
    condition,
  };

  return (
    <>
      <section className="df-discovery-hero" aria-labelledby="discovery-heading">
        <div className="df-discovery-hero__brand df-brand df-brand--dark" style={{ animation: 'none' }}>
          <span className="df-brand__mark" aria-hidden="true">DF</span>
          <span className="df-brand__wordmark">DealFusion</span>
        </div>
        <h1 id="discovery-heading">Sharper prices, verified by shoppers like you</h1>
        <p>Community heat scores, merchant clarity, and price history — without the clutter.</p>
      </section>

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }} className="df-deal-layout">
        <aside style={{ width: 260, flexShrink: 0 }} className="df-filters-aside">
          <DealFilterPanel
            filters={filterValues}
            onChange={handleFilters}
          />
        </aside>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <Space size={6} wrap>
              <Button
                className="df-mobile-filters-btn"
                icon={<FilterOutlined />}
                onClick={() => setFiltersOpen(true)}
                style={{
                  borderRadius: 8,
                  height: 40,
                  fontWeight: 600,
                  display: 'none',
                }}
                aria-label="Open filters"
              >
                Filters
              </Button>
              {SORT_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type={sort === opt.value ? 'primary' : 'default'}
                  size="middle"
                  icon={opt.icon}
                  onClick={() => handleSortChange(opt.value)}
                  style={{
                    borderRadius: 8,
                    padding: '0 14px',
                    height: 40,
                    fontSize: 13,
                    fontWeight: sort === opt.value ? 600 : 500,
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </Space>

            {isAuthenticated && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/deals/submit')}
                style={{
                  borderRadius: 8,
                  fontWeight: 600,
                  height: 40,
                  background: 'var(--df-accent)',
                  borderColor: 'var(--df-accent)',
                }}
              >
                Submit a Deal
              </Button>
            )}
          </div>

          {search && (
            <Text style={{ display: 'block', marginBottom: 16, color: 'var(--df-text-secondary)' }}>
              Results for “{search}”
            </Text>
          )}

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
              <Spin size="large" tip="Loading deals…" />
            </div>
          ) : !data?.items?.length ? (
            <div style={{ padding: '80px 0', textAlign: 'center' }}>
              <Empty
                description="No deals found — try adjusting your filters"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: 20,
                }}
              >
                {data.items.map((deal) => (
                  <div
                    key={deal.publicId}
                    className="df-feed-item"
                    onClick={() => navigate(`/deals/${deal.publicId}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/deals/${deal.publicId}`); }}
                    role="link"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                  >
                    <DealCard deal={deal} />
                  </div>
                ))}
              </div>

              {data?.page?.hasMore && (
                <div style={{ textAlign: 'center', marginTop: 32 }}>
                  <Button
                    onClick={handleLoadMore}
                    loading={isFetching}
                    size="large"
                    style={{ borderRadius: 10, padding: '0 36px', height: 44, fontWeight: 600 }}
                  >
                    Load More Deals
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Drawer
        title="Filters"
        placement="bottom"
        height="82%"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        styles={{ body: { paddingTop: 4 } }}
      >
        <DealFilterPanel filters={filterValues} onChange={handleFilters} />
      </Drawer>

      <style>{`
        @media (max-width: 900px) {
          .df-filters-aside { display: none; }
          .df-mobile-filters-btn { display: inline-flex !important; }
        }
      `}</style>
    </>
  );
}
